import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	BOOKING_LIMITS,
	BOOKING_WHERE_LABELS,
	type PublicBookingDto,
	type PublicBookingGuestDto,
	type PublicBookingPageDto,
} from '@supply-mover-2-0/shared';
import { useTheme, useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { FormEvent, JSX } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BookingTimePicker } from '../components/booking/BookingTimePicker';
import { PaymentLinkShell } from '../components/paymentLinks/PaymentLinkShell';
import { useTurnstile } from '../hooks/useTurnstile';
import { formatDay, formatMeeting, formatTime, timezoneLabel, ymdInZone } from '../lib/bookingTimes';
import {
	BookingRequestError,
	createBooking,
	fetchBookingPage,
	fetchBookingTimes,
} from '../services/bookingService';

const INPUT_CLASS =
	'mt-1 block w-full rounded-lg border border-brand-border bg-brand-input text-default px-3 py-2 text-sm';

export function BookingNotice({ title, body }: { title: string; body: string }): JSX.Element {
	const { t } = useTranslation();

	return (
		<PaymentLinkShell secureLabel={null} showSignIn={false}>
			<div className="bg-brand-surface border border-brand-border rounded-xl p-6 sm:p-8 text-center space-y-2">
				<h1 className="text-xl font-semibold text-default">{t(title)}</h1>
				<p className="text-brand-muted">{t(body)}</p>
			</div>
		</PaymentLinkShell>
	);
}

function BookingSummary({ page, guest, onNotYou }: { page: PublicBookingPageDto; guest: PublicBookingGuestDto | null; onNotYou: () => void }): JSX.Element {
	const { t } = useTranslation();

	return (
		<section className="bg-brand-surface border border-brand-border rounded-xl p-5 self-start">
			<p className="text-sm text-brand-muted">
				{page.showLine != null ? t(page.showLine) : `${t(page.hostName)}, ${t(page.companyName)}`}
			</p>
			<h1 className="mt-1 text-xl font-bold text-default">{t(page.title)}</h1>
			<p className="mt-1 text-sm text-brand-muted">
				{t('{{count}} minutes', { count: page.lengthMinutes })} · {t(page.whereDetail ?? BOOKING_WHERE_LABELS[page.whereKind])}
			</p>
			{page.description != null && page.description !== '' ? (
				<p className="mt-3 text-sm text-default whitespace-pre-line">{t(page.description)}</p>
			) : null}
			{guest?.name != null ? (
				<>
					<p className="mt-4 text-sm text-default">
						{t('Hi {{name}}', { name: guest.name })}
						{guest.company != null ? `, ${t(guest.company)}` : ''}
					</p>
					<p className="text-xs text-brand-muted">
						{t('Not you?')}{' '}
						<button type="button" onClick={onNotYou} className="text-portal-accent hover:underline">
							{t('Use a different name')}
						</button>
					</p>
				</>
			) : null}
		</section>
	);
}

/** PD-17646 — `/book/:person/:type`: a guest picks a time and books, without a login. */
export function BookingPage(): JSX.Element {
	const { t } = useTranslation();
	const { isDark } = useTheme();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { person = '', type = '' } = useParams();
	const [search] = useSearchParams();
	const inviteToken = search.get('t');
	// PD-17648 — a time offered in an email: the link books it in one click.
	const offeredStart = search.get('at');

	const { data: page, isLoading, error } = useQuery({
		queryKey: ['booking-page', person, type, inviteToken],
		queryFn: () => fetchBookingPage(person, type, inviteToken),
		retry: false,
	});

	const [timezone, setTimezone] = useState(() => Intl.DateTimeFormat().resolvedOptions().timeZone);
	const [start, setStart] = useState<string | null>(null);
	const [useInvite, setUseInvite] = useState(true);
	const [name, setName] = useState('');
	const [company, setCompany] = useState('');
	const [email, setEmail] = useState('');
	const [phone, setPhone] = useState('');
	const [answers, setAnswers] = useState<Record<string, string>>({});
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState<string | null>(null);
	const [fieldInError, setFieldInError] = useState<string | null>(null);
	const [nearest, setNearest] = useState<Array<string>>([]);
	const [booked, setBooked] = useState<{ booking: PublicBookingDto; manageToken: string | null } | null>(null);
	const triedOffer = useRef(false);

	const turnstile = useTurnstile({
		siteKey: start != null ? (page?.turnstileSiteKey ?? null) : null,
		action: 'booking',
		theme: isDark ? 'dark' : 'light',
	});

	const guest = useInvite ? (page?.guest ?? null) : null;

	useEffect(() => {
		// An old link keeps working: the address moves to the current name.
		if (page?.redirectTo != null) {
			const query = inviteToken != null ? `?t=${encodeURIComponent(inviteToken)}` : '';

			void navigate(`${page.redirectTo}${query}`, { replace: true });
		}
	}, [inviteToken, navigate, page?.redirectTo]);

	useEffect(() => {
		setName(guest?.name ?? '');
		setCompany(guest?.company ?? '');
		setEmail(guest?.email ?? '');
		setPhone(guest?.phone ?? '');
	}, [guest]);

	const fetchTimes = useCallback(
		(window: { from: string; to: string }) => fetchBookingTimes(person, type, window, inviteToken),
		[inviteToken, person, type],
	);

	const pickerKey = useMemo(() => [person, type, inviteToken], [inviteToken, person, type]);

	const book = useCallback(async (when: string, details: { name: string; company: string; email: string; phone: string }): Promise<void> => {
		setSaving(true);
		setMessage(null);
		setFieldInError(null);
		setNearest([]);

		try {
			const result = await createBooking(person, type, {
				start: when,
				...details,
				timezone,
				answers,
				t: useInvite ? inviteToken : null,
				turnstileToken: await turnstile.getToken(),
			});

			setBooked(result);
		} catch (caught) {
			turnstile.reset();

			if (caught instanceof BookingRequestError) {
				setMessage(caught.message);
				setFieldInError(caught.field);
				setNearest(caught.nearest);

				if (caught.code === 'slot_taken') {
					setStart(null);
					await queryClient.invalidateQueries({ queryKey: ['booking-times'] });
				}
			} else {
				setMessage('Something went wrong. Please try again.');
			}
		} finally {
			setSaving(false);
		}
	}, [answers, inviteToken, person, queryClient, timezone, turnstile, type, useInvite]);

	const submit = async (event: FormEvent): Promise<void> => {
		event.preventDefault();

		if (start != null) {
			await book(start, { name, company, email, phone });
		}
	};

	// The email's time goes straight through for a guest the link knows. Done here, after
	// the page has run, so a mail scanner that only fetches the address books nothing. A
	// guest the link doesn't name gets the form with that time chosen.
	useEffect(() => {
		if (offeredStart == null || page == null || triedOffer.current) {
			return;
		}

		triedOffer.current = true;
		setStart(offeredStart);

		if (page.guest?.name != null && page.guest.email != null) {
			void book(offeredStart, {
				name: page.guest.name,
				company: page.guest.company ?? '',
				email: page.guest.email,
				phone: page.guest.phone ?? '',
			});
		}
	}, [book, offeredStart, page]);

	if (isLoading) {
		return <PaymentLinkShell secureLabel={null} showSignIn={false} wide><div className="min-h-64" /></PaymentLinkShell>;
	}

	if (page == null) {
		if (error instanceof BookingRequestError && error.code === 'invite_required') {
			return <BookingNotice title="This page is by invitation" body="Please use the link you were sent." />;
		}

		return <BookingNotice title="This booking page is not available" body="Check that you opened the whole link, or contact the person who sent it." />;
	}

	if (booked != null) {
		return (
			<PaymentLinkShell secureLabel={null} showSignIn={false}>
				<div className="bg-brand-surface border border-brand-border rounded-xl p-6 sm:p-8 text-center space-y-3" data-testid="booking-confirmed">
					<FontAwesomeIcon icon={faCircleCheck} className="text-3xl text-portal-accent" />
					<h1 className="text-xl font-semibold text-default">{t("You're booked")}</h1>
					<p className="text-default">
						{t(booked.booking.title)} {t('with')} {t(booked.booking.hostName)}
					</p>
					<p className="text-default font-medium">{t(formatMeeting(booked.booking.start, timezone))}</p>
					<p className="text-sm text-brand-muted">
						{t('A confirmation with a calendar invite is on its way to {{email}}.', { email: booked.booking.guestEmail })}
					</p>
					{booked.manageToken != null ? (
						<Link to={`/book/meeting/${booked.manageToken}`} className="text-sm text-portal-accent hover:underline">
							{t('Reschedule or cancel')}
						</Link>
					) : null}
				</div>
			</PaymentLinkShell>
		);
	}

	return (
		<PaymentLinkShell secureLabel={null} showSignIn={false} wide>
			<div className="grid gap-4 md:grid-cols-[20rem_1fr]">
				<BookingSummary
					page={page}
					guest={guest}
					onNotYou={() => {
						setUseInvite(false);
					}}
				/>
				<div className="space-y-4 min-w-0">
					{start == null ? (
						<>
							{message != null ? (
								<p className="rounded-lg border border-brand-border bg-brand-surface px-4 py-3 text-sm text-default" role="alert">
									{t(message)}
								</p>
							) : null}
							{nearest.length > 0 ? (
								<div className="flex flex-wrap gap-2" data-testid="booking-nearest">
									{nearest.map((option) => (
										<button
											key={option}
											type="button"
											onClick={() => {
												setStart(option);
												setMessage(null);
											}}
											className="rounded-lg border border-portal-accent text-portal-accent px-3 py-2 text-sm font-semibold"
										>
											{t(formatMeeting(option, timezone))}
										</button>
									))}
								</div>
							) : null}
							<BookingTimePicker
								queryKey={pickerKey}
								fetchTimes={fetchTimes}
								hostTimezone={page.hostTimezone}
								timezone={timezone}
								onTimezoneChange={setTimezone}
								selected={start}
								onSelect={setStart}
							/>
						</>
					) : (
						<form
							onSubmit={(event) => {
								void submit(event);
							}}
							className="bg-brand-surface border border-brand-border rounded-xl p-5 space-y-3"
						>
							<div className="flex items-baseline justify-between gap-3">
								<h2 className="font-semibold text-default">
									{t(formatDay(ymdInZone(new Date(start), timezone)))} {t('at')} {t(formatTime(start, timezone))}
								</h2>
								<button
									type="button"
									onClick={() => {
										setStart(null);
									}}
									className="text-sm text-portal-accent hover:underline"
								>
									{t('Pick another time')}
								</button>
							</div>
							<p className="text-xs text-brand-muted">{t(timezoneLabel(timezone))}</p>
							<label className="block text-sm text-brand-muted">
								{t('Name')}
								<input required maxLength={BOOKING_LIMITS.nameLength} value={name} onChange={(event) => { setName(event.target.value); }} autoComplete="name" className={INPUT_CLASS} aria-invalid={fieldInError === 'name'} />
							</label>
							<label className="block text-sm text-brand-muted">
								{t('Company')}
								<input maxLength={BOOKING_LIMITS.companyLength} value={company} onChange={(event) => { setCompany(event.target.value); }} autoComplete="organization" className={INPUT_CLASS} />
							</label>
							<label className="block text-sm text-brand-muted">
								{t('Email')}
								<input required type="email" maxLength={BOOKING_LIMITS.emailLength} value={email} onChange={(event) => { setEmail(event.target.value); }} autoComplete="email" className={INPUT_CLASS} aria-invalid={fieldInError === 'email'} />
							</label>
							<label className="block text-sm text-brand-muted">
								{t('Mobile, for a reminder text (optional)')}
								<input type="tel" maxLength={BOOKING_LIMITS.phoneLength} value={phone} onChange={(event) => { setPhone(event.target.value); }} autoComplete="tel" className={INPUT_CLASS} aria-invalid={fieldInError === 'phone'} />
							</label>
							{page.questions.map((question) => (
								<label key={question.id} className="block text-sm text-brand-muted">
									{t(question.label)}
									{question.kind === 'long' ? (
										<textarea
											required={question.required}
											maxLength={BOOKING_LIMITS.answerLength}
											rows={3}
											value={answers[question.id] ?? ''}
											onChange={(event) => {
												setAnswers((previous) => ({ ...previous, [question.id]: event.target.value }));
											}}
											className={INPUT_CLASS}
											aria-invalid={fieldInError === question.id}
										/>
									) : (
										<input
											required={question.required}
											maxLength={BOOKING_LIMITS.answerLength}
											value={answers[question.id] ?? ''}
											onChange={(event) => {
												setAnswers((previous) => ({ ...previous, [question.id]: event.target.value }));
											}}
											className={INPUT_CLASS}
											aria-invalid={fieldInError === question.id}
										/>
									)}
								</label>
							))}
							<div ref={turnstile.containerRef} />
							{turnstile.failed ? (
								<p className="text-sm text-default" role="alert">
									{t('The security check could not load. Please reload the page.')}
								</p>
							) : null}
							{message != null ? (
								<p className="text-sm text-default" role="alert">
									{t(message)}
								</p>
							) : null}
							<button
								type="submit"
								disabled={saving}
								className="w-full rounded-lg bg-portal-accent hover:bg-portal-accent-hover text-white py-2.5 font-semibold disabled:opacity-60"
							>
								{saving ? t('Booking...') : t('Book {{time}}', { time: formatTime(start, timezone) })}
							</button>
							<p className="text-xs text-brand-muted">
								{t("You'll get an email with a calendar invite. Reschedule or cancel from that email.")}
							</p>
						</form>
					)}
				</div>
			</div>
		</PaymentLinkShell>
	);
}
